import useUserData from 'hooks/useUserData';
import { InsightFilterType, InsightType } from '../apis/PythonInsightsAPI';
import { insightsQueryKeys } from './query-keys';
import { QueryFunctionContext, QueryKey, useQuery } from 'react-query';
import { InsightsAPI, PythonInsightsAPI } from '..';
import { useMemo } from 'react';
import { AxiosResponse } from 'axios';
import { useConfigQuery } from '@buildwise/libs/configurations';
import { ConfigEntryType } from 'config/config';

export const getUniFormatDisplayTitle = (title: string) =>
  title.indexOf('-') > -1 ? title.substring(title.indexOf('-') + 1).trim() : title;

export type MappedInsightType = {
  displayTitle?: string;
  title: string;
  count: number;
  children?: MappedInsightType[];
  insightSummaryFilters: InsightFilterType[];
};

type InsightMetaDataType = {
  children?: InsightMetaDataType[];
  count: number;
  title: string;
};

export type InsightsResponseType = AxiosResponse<
  {
    insightsGroupedMetaData: {
      groupByFieldName: string;
      metaData: InsightMetaDataType[];
      totalLevels: number;
    };
  },
  Error
>;

export type GetInsightsQueryFnType = (options: {
  companyId: string;
  context: QueryFunctionContext;
  groupBy: string;
  filters?: InsightFilterType[];
  novaSearchKeyWord?: string;
  includeBWInsights: boolean;
}) => Promise<InsightsResponseType> | undefined;

export type GetInsightsQueryKeyType = (options: {
  companyId: string;
  groupBy: string;
  filters?: InsightFilterType[];
  novaSearchKeyWord?: string;
  includeBWInsights: boolean;
}) => QueryKey;

function useInsightsQuery({
  filters,
  groupBy,
  includeBWInsights,
  insightsModule,
  novaSearchKeyWord,
  projectId,
  shouldFetch = true,
  getInsightsQueryFn,
  getInsightsQueryKey,
}: {
  filters?: InsightFilterType[];
  groupBy: string;
  includeBWInsights: boolean;
  insightsModule?: ConfigEntryType | undefined;
  novaSearchKeyWord?: string;
  projectId?: string;
  shouldFetch?: boolean;
  getInsightsQueryFn?: GetInsightsQueryFnType;
  getInsightsQueryKey?: GetInsightsQueryKeyType;
}) {
  const { companyId } = useUserData();
  const { data: insightsData, ...rest } = useQuery<InsightsResponseType>({
    queryKey:
      getInsightsQueryKey?.({
        companyId,
        groupBy,
        filters,
        novaSearchKeyWord,
        includeBWInsights,
      }) ||
      (novaSearchKeyWord
        ? insightsQueryKeys.insightsSearch({
            companyId,
            groupBy,
            novaSearchKeyWord,
            filters,
            includeBWInsights,
          })
        : insightsQueryKeys.insights({ companyId, groupBy, filters, includeBWInsights })),
    queryFn: (context) =>
      getInsightsQueryFn?.({
        companyId,
        context,
        groupBy,
        filters,
        novaSearchKeyWord,
        includeBWInsights,
      }) ||
      (novaSearchKeyWord
        ? PythonInsightsAPI.getInsightsSearch({
            companyId,
            filters,
            groupBy,
            includeBWInsights,
            novaSearchKeyWord,
          })
        : InsightsAPI.getInsights({ companyId, groupBy, filters, projectId, includeBWInsights })),
    staleTime: 60 * 1000,
    enabled: !!(companyId && groupBy) && shouldFetch,
  });
  const { activeUniqueEntries } = useConfigQuery({
    companyId,
    configName: insightsModule?.referenceObject?.groupBy?.configRefName,
  });
  const groupByEntry = useMemo(
    () => activeUniqueEntries.find((entry) => entry.entryCode === groupBy),
    [activeUniqueEntries, groupBy],
  );

  const insights = useMemo(
    () => insightsData?.data?.insightsGroupedMetaData?.metaData || [],
    [insightsData],
  );

  const cleanMappedInsights = (mappedInsights: MappedInsightType[]): MappedInsightType[] => {
    return mappedInsights
      .filter((insight) => insight?.title?.trim())
      .map((insight) => ({
        ...insight,
        children:
          insight.children && insight.children.length ? cleanMappedInsights(insight.children) : [],
      }));
  };

  const mappedInsights = useMemo(() => {
    const _mappedInsights: MappedInsightType[] = [];

    _mappedInsights.push(
      ...insights.map((insight) => ({
        ...(groupBy === InsightType.UniFormat && {
          displayTitle: getUniFormatDisplayTitle(insight.title),
        }),
        title: insight.title,
        count: insight.count,
        insightSummaryFilters: [
          {
            filterName: groupByEntry?.referenceObject?.filterNameByLevels?.[0] || '',
            filterValues: [insight.title],
          },
        ],
        children:
          insight.children?.map((_insight) => ({
            displayTitle: getUniFormatDisplayTitle(_insight.title),
            title: _insight.title,
            count: _insight.count,
            insightSummaryFilters: [
              {
                filterName: groupByEntry?.referenceObject?.filterNameByLevels?.[1] || '',
                filterValues: [_insight.title],
              },
            ],
            children:
              _insight.children?.map((__insight) => ({
                displayTitle: getUniFormatDisplayTitle(__insight.title),
                title: __insight.title,
                count: __insight.count,
                insightSummaryFilters: [
                  {
                    filterName: groupByEntry?.referenceObject?.filterNameByLevels?.[2] || '',
                    filterValues: [__insight.title],
                  },
                ],
              })) || [],
          })) || [],
      })),
    );

    return cleanMappedInsights(_mappedInsights);
  }, [insights, groupBy, groupByEntry]);

  return {
    insights,
    mappedInsights,
    ...rest,
  };
}

export default useInsightsQuery;
